<template>
  <vs-popup :active.sync="isActive" title="" @close="close">
    <div class="state-change-popup">

      <button class="popup-close" @click="close">✕</button>

      <div v-if="!isProcessing" class="popup-body">
        <div :class="['popup-icon', isCanceling ? 'icon-danger' : 'icon-success']">
          <feather-icon :icon="isCanceling ? 'AlertTriangleIcon' : 'CheckCircleIcon'" svgClasses="w-10 h-10" />
        </div>
        <h3 class="popup-title">{{ isCanceling ? 'Anular facturación' : 'Confirmar facturación' }}</h3>
        <p class="message">{{ popupMessage }}</p>

        <div v-if="!isCanceling && billingComments.length" class="billing-comments-note">
          <p class="billing-comments-title">Comentario de facturación:</p>
          <ul>
            <li v-for="(item, index) in billingComments" :key="index">
              <strong v-if="billingComments.length > 1">Cierre #{{ item.number }}: </strong>{{ item.comment }}
            </li>
          </ul>
        </div>

        <div v-if="isCanceling" class="cancel-comment">
          <label class="cancel-comment-label">Comentario</label>
          <vs-textarea
            v-model="cancellationComment"
            :rows="3"
            maxlength="1000"
            placeholder="Indique el motivo de anulación, campo obligatorio"
          />
          <p v-if="cancellationComment.trim().length > 0 && cancellationComment.trim().length < 10" class="cancel-comment-hint">
            Mínimo 10 caracteres.
          </p>
        </div>
      </div>

      <div v-if="isProcessing" class="processing">
        <vs-loading></vs-loading>
        <p>Procesando cambio de estado...</p>
      </div>

      <div v-if="errorMessage" class="error-message">
        <vs-alert color="danger" class="mb-3">{{ errorMessage }}</vs-alert>
      </div>

      <div v-if="!isProcessing" class="popup-actions">
        <vs-button type="border" @click="close">Cancelar</vs-button>
        <vs-button :color="isCanceling ? 'danger' : 'primary'" @click="handleConfirm" :disabled="!canProceed">
          {{ isCanceling ? 'Sí, anular' : 'Sí, facturar' }}
        </vs-button>
      </div>
    </div>
  </vs-popup>
</template>

<script>
import appConfig from '@/../appConfig.js'
import { BILLING_STATUS } from '@/constants/billingAndCharges'

export default {
  name: 'BillingStateChangePopup',
  props: {
    popupActive: {
      type: Boolean,
      default: false
    },
    selectedRows: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isActive: this.popupActive,
      isProcessing: false,
      errorMessage: '',
      newStatus: null,
      cancellationComment: ''
    }
  },
  computed: {
    popupMessage() {
      if (!this.selectedRows || this.selectedRows.length === 0) return ''
      const firstRow = this.selectedRows[0]
      const currentStatus = firstRow.billing_status
      if (currentStatus === BILLING_STATUS.BILLED) {
        return `¿Estás seguro de que deseas anular la facturación de ${this.selectedRows.length} cierre(s)?\n\nEsta acción solo se puede revertir si no hay cobros activos.`
      } else {
        return `¿Estás seguro de que deseas marcar ${this.selectedRows.length} cierre(s) como facturado?\n\nEsta acción bloqueará la anulación del cierre.`
      }
    },
    canProceed() {
      if (!this.selectedRows || this.selectedRows.length === 0 || this.isProcessing) return false
      // Cancellation requires a reason of at least 10 characters
      if (this.isCanceling) return this.cancellationComment.trim().length >= 10
      return true
    },
    billingComments() {
      return (this.selectedRows || [])
        .filter(row => row.billing_comment)
        .map(row => ({ number: row.closure_number, comment: row.billing_comment }))
    },
    isCanceling() {
      if (!this.selectedRows || this.selectedRows.length === 0) return false
      return this.selectedRows[0].billing_status === BILLING_STATUS.BILLED
    }
  },
  watch: {
    popupActive(val) {
      this.isActive = val
      if (val) {
        this.errorMessage = ''
        this.cancellationComment = ''
      }
    },
    isActive(val) {
      this.$emit('update:popupActive', val)
    }
  },
  methods: {
    async handleConfirm() {
      this.isProcessing = true
      this.errorMessage = ''
      try {
        const firstRow = this.selectedRows[0]
        const currentStatus = firstRow.billing_status
        const newStatus = currentStatus === BILLING_STATUS.BILLED ? 'canceled' : 'billed'
        const body = { status: newStatus }
        if (newStatus === 'canceled') {
          body.cancellation_comment = this.cancellationComment.trim()
        }
        const promises = this.selectedRows.map(row =>
          this.$http.patch(
            `${appConfig.apiUrl}/sales-closure-billings/${row.id}`,
            body
          )
        )
        await Promise.all(promises)
        this.$vs.notify({
          title: 'Éxito',
          text: `Se actualizaron ${this.selectedRows.length} cierre(s) correctamente`,
          color: 'success'
        })
        this.$emit('refresh')
        this.close()
      } catch (error) {
        console.error('Error updating billing status:', error)
        if (error.response && error.response.status === 422) {
          this.errorMessage = (error.response.data && error.response.data.message) || 'No se puede realizar la acción'
        } else {
          this.errorMessage = 'Error al actualizar el estado. Por favor, intenta de nuevo.'
        }
      } finally {
        this.isProcessing = false
      }
    },
    close() {
      this.isActive = false
      this.errorMessage = ''
    }
  }
}
</script>

<style lang="scss">
@import '@/views/css/billing-charges-state-popup.scss';
</style>

<style scoped>
::v-deep .vs-popup--close {
  display: none !important;
}

.billing-comments-note {
  margin-top: 12px;
  padding: 10px 12px;
  background: #f0f7ff;
  border-left: 3px solid #0096f5;
  border-radius: 4px;
  text-align: left;
  font-size: 13px;
}

.billing-comments-title {
  font-weight: 700;
  color: #0096f5;
  margin-bottom: 4px;
}

.billing-comments-note ul {
  margin: 0;
  padding-left: 16px;
  list-style: disc;
}

.cancel-comment {
  width: 100%;
  text-align: left;
  margin-top: 12px;
}

.cancel-comment-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
}

.cancel-comment-hint {
  color: #EA5455;
  font-size: 12px;
  margin-top: 4px;
}
</style>
